import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserRole } from '../../types';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface RequireRoleProps {
  allow: UserRole | UserRole[];
  allowAnonymous?: boolean;
  children: React.ReactNode;
}

// Role-aware route guard. Redirects unauthenticated users to /login with a
// return URL, and authenticated users whose role is not in `allow` to
// /dashboard (where the role-aware dashboard can land them appropriately).
//
// `allowAnonymous` mirrors the backend's `require_role_or_anonymous`
// helper: when true, unauthenticated callers fall through and render the
// child surface. The role check still fires for authenticated callers so
// disallowed roles get redirected. Use this for public-search-style
// routes that admit anonymous traffic but exclude specific authenticated
// roles.
export const RequireRole: React.FC<RequireRoleProps> = ({
  allow,
  allowAnonymous = false,
  children,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingSpinner />;

  if (!isAuthenticated || !user) {
    if (allowAnonymous) return <>{children}</>;
    const redirect = `?redirect=${encodeURIComponent(location.pathname)}`;
    return <Navigate to={`/login${redirect}`} replace />;
  }

  const allowed = Array.isArray(allow) ? allow : [allow];
  if (!allowed.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};
